import React, { useEffect, useMemo, useRef } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import moment from "moment-timezone";
import "moment/locale/hu";
import useTranslation from "../../../../hooks/useTranslation";

moment.locale("hu");

const DeviceChartForContainerView = ({
  chartData,
  allSensorData
}) => {
  const { t } = useTranslation("locationsPage");
  const chartRef = useRef(null);
  const uplotInstance = useRef(null);

  /*
  console.log("Chart Data:", chartData);
  console.log("Time Format:", timeFormat);
  console.log("Start Date for Graph Title:", startDateForGraphTitle);
  console.log("End Date for Graph Title:", endDateForGraphTitle); */
  //const sensors = Array.from(new Set(chartData.map(item => `${item.probeid}-${item.sens_num}`)));

  // Helper: generate N visually distinct colors
  function generateColors(n) {
    const colors = [];
    for (let i = 0; i < n; i++) {
      // Use HSL for evenly spaced hues
      colors.push(`hsl(${(i * 360) / n}, 70%, 50%)`);
    }
    return colors;
  }

  // Helper: round timestamp to the hour (local time, de tooltiphez is ezt használjuk)
  function roundToHour(ts) {
    const date = new Date(ts);
    date.setMinutes(0, 0, 0); // helyi idő szerint nulláz
    return date.getTime();
  }

  // If there are any temperature sensors (sens_type === 2), we only want to show those in the chart, but we still want to keep all data for the tooltip. So we create a filtered dataset for the chart rendering, but keep the original for tooltips.
  const filteredData = useMemo(() => {
    if (chartData.some(item => item.sens_type === 2)) {
      return chartData.filter(item => item.sens_type === 2);
    }
    return chartData;
  }, [chartData]);

  // 1. Get all unique hourly timestamps
  const hourlyTimestamps = useMemo(
    () =>
      Array.from(
        new Set(filteredData.map((item) => roundToHour(item.sens_timestamp)))
      ).sort((a, b) => a - b),
    [filteredData]
  );

  // 2. Build lookup for each temperature sensor, bucketed by hour
  const sortedSensors = useMemo(
    () =>
      Array.from(
        new Set(filteredData.map((item) => `${item.probeid}-${item.sens_num}`))
      ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [filteredData]
  );

  // Generate colors for each sensor
  const sensorColors = useMemo(
    () => generateColors(sortedSensors.length),
    [sortedSensors]
  );

  const dataLookup = useMemo(() => {
    const lookup = {};
    filteredData.forEach((item) => {
      const key = `${item.probeid}-${item.sens_num}`;
      const hourTs = roundToHour(item.sens_timestamp);
      if (!lookup[key]) lookup[key] = {};
      lookup[key][hourTs] = item.sens_value;
    });
    return lookup;
  }, [filteredData]);

  // 3. Build series aligned to hourly timestamps
  const series = useMemo(
    () =>
      sortedSensors.map((sensorKey) =>
        hourlyTimestamps.map((ts) => dataLookup[sensorKey]?.[ts] ?? null)
      ),
    [sortedSensors, hourlyTimestamps, dataLookup]
  );

  const processedData = useMemo(
    () => [hourlyTimestamps.map((ts) => ts / 1000), ...series],
    [hourlyTimestamps, series]
  );

  // console.log("Processed Data for Chart:", processedData);

  useEffect(() => {
    if (!chartRef.current) return;

    // Destroy previous chart
    if (uplotInstance.current) {
      uplotInstance.current.destroy();
    }

    // Tooltip element
    let tooltip = document.createElement("div");
    tooltip.style.position = "absolute";
    tooltip.style.pointerEvents = "none";
    tooltip.style.background = "rgba(0,0,0,0.8)";
    tooltip.style.color = "#fff";
    tooltip.style.padding = "4px 8px";
    tooltip.style.borderRadius = "4px";
    tooltip.style.fontSize = "12px";
    tooltip.style.zIndex = 10;
    tooltip.style.display = "none";
    chartRef.current.appendChild(tooltip);

    // uPlot options
    const opts = {
      title: t("measured_value_for_graph"),
      width: chartRef.current.offsetWidth || 600,
      height: 400,
      scales: {
        x: { time: true },
      },
      cursor: {
        drag: {
          x: true, // enable horizontal drag-to-zoom
          y: false,
        },
        lock: true, // lock cursor to nearest data point
      },
      select: {
        show: true, // show selection rectangle
        over: true, // allow selection over chart
      },
      series: [
        { label: t("time") },
        // Use sortedSensors for legend order
        ...sortedSensors.map((sensorKey, idx) => ({
          label: sensorKey,
          stroke: sensorColors[idx],
        })),
      ],
      axes: [{}, {}],
      hooks: {
        setCursor: [
          (u) => {
            const { left, top, idx } = u.cursor;
            if (idx == null || idx < 0) {
              tooltip.style.display = "none";
              return;
            }
            // Show tooltip only if there is extra data (non-temperature) for this timestamp
            const ts = roundToHour(u.data[0][idx] * 1000);
            const existExtraData = chartData && chartData.some(d => d.sens_type !== 2 && roundToHour(d.sens_timestamp) === ts);
            if (!existExtraData) {
              tooltip.style.display = "none";
              return;
            }
            let html = `<b>${t("time")}</b>: ${moment.unix(u.data[0][idx]).format("YYYY-MM-DD HH:mm")}`;
            // Tooltip for extra sensor types
            if (chartData) {
              // Rounded timestamp
              const ts = roundToHour(u.data[0][idx] * 1000);
              const types = [
                //{ type: 2, label: '°C', color: '#FF4500' },
                { type: 1, label: 'V', color: '#FFD700' },
                { type: 249, label: 'dBm', color: '#1E90FF' },
                { type: 253, label: 'BS', color: '#32CD32' },
              ];
              types.forEach(({ type, label, color }) => {
                chartData.filter(d => d.sens_type === type && roundToHour(d.sens_timestamp) === ts)
                  .forEach(d => {
                    html += `<br/><span style='color:${color}'>${label} [${d.probeid}-${d.sens_num}]</span>: <b>${d.sens_value}</b>`;
                  });
              });
            }
            tooltip.innerHTML = html;
            tooltip.style.left = left + 55 + "px";
            tooltip.style.top = top - 40 + "px";
            tooltip.style.display = "block";
          },
        ],
        mouseLeave: [
          () => {
            tooltip.style.display = "none";
          },
        ],
      },
    };

    uplotInstance.current = new uPlot(opts, processedData, chartRef.current);

    // Cleanup
    return () => {
      if (uplotInstance.current) {
        uplotInstance.current.destroy();
        uplotInstance.current = null;
      }
      if (tooltip && tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }
    };
  }, [processedData, t, sortedSensors, sensorColors, allSensorData, chartData]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div ref={chartRef} />
    </div>
  );
};

export default DeviceChartForContainerView;
